/**
 * Teacher view: the roster with each student's status, and a per-student page
 * (fact map, stage per table, review stage today, recent answers, remove student).
 */
import { UNITS, stagesFor, unitById, reviewStageIndex, PATTERN_STAGES } from "../engine/curriculum";
import { summary, streak, warmDoneToday, type StudentProgress } from "../engine/progress";
import type { Store, Student, AttemptRow } from "../data/store";
import { factMapHTML, hasDivision, LEGEND } from "./factmap";
import type { SkillAttemptRow } from "../data/store";
import { LEVELS, anyLesson, lessonNo, lessonName } from "../lessons/levels";
import { upNextLesson } from "./home";
import { MISCONCEPTIONS } from "../lessons/misconceptions";
import { SKILLS } from "../lessons/skills";
import { lessonProgress, checkScore } from "./lessonView";
import { DOMAINS, DOMAIN_NAMES } from "../lessons/level3/checkups";
import type { CheckItem, Lesson } from "../lessons/types";
import { esc } from "./keypad";
import { mountStudy } from "./study";
import { TABLES, factsSummary, taken } from "../lessons/factsTest";

export interface TeacherOptions {
  store: Store;
  email: string | null;
  onBack: () => void;
  onOpenStudent: (s: Student) => void;
}

const fmtWhen = (iso: string) => {
  const d = new Date(iso), now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : d.toLocaleDateString([], { month: "short", day: "numeric" });
};
const stageName = (i: number) => (i < 0 ? `Review, ${PATTERN_STAGES[-1 - i]?.name ?? "?"}` : i === 9 ? "Mixed review" : i >= 20 ? `Division, Stage ${i - 19}` : `Stage ${i + 1}`);
const factText = (a: AttemptRow) => (a.stage >= 20 ? `${a.a * a.b} ÷ ${a.a}` : `${a.a} × ${a.b}`);

export async function mountTeacher(root: HTMLElement, o: TeacherOptions): Promise<void> {
  const students = await o.store.listStudents();
  const all = await o.store.loadProgressMany(students.map((s) => s.id));
  const rows = students.map((s) => ({ s, p: all.get(s.id)! }));
  root.innerHTML = `
  <header class="band"><div class="wrap">
    <div><div class="brand">THE TIGARD METHOD<sup>™</sup></div><div class="tagline">Teacher view</div></div>
    <div class="who">${o.email ? `<span>${esc(o.email)}</span>` : ""}<button type="button" id="back">Student picker</button></div>
  </div></header>
  <main><div class="wrap" style="display:block">
    <section id="study" style="margin-bottom:28px"></section>
    <h1 style="margin-bottom:6px">Roster</h1>
    <p class="muted" style="margin-top:0">${rows.length} student${rows.length === 1 ? "" : "s"}. Tap a name for the fact map and recent answers.</p>
    <div class="mapwrap"><table class="roster" id="roster">
      <tr><th>Student</th><th>Check-ups</th><th>Lessons</th><th>Unit</th><th>Stage</th><th>Warm-up today</th><th>Streak</th><th>Fluent</th><th>Correct</th><th>Revisit</th></tr>
      ${rows.map(({ s, p }) => {
        const u = unitById(p.currentUnit), us = p.units[u.id]?.stage ?? 0, sm = summary(p);
        return `<tr data-id="${s.id}"><td><button type="button" class="linkish" data-open="${s.id}">${esc(s.firstName)}</button>${s.grade != null ? `<span class="muted"> · Gr ${s.grade}</span>` : ""}</td>
          <td>${esc(checkLine(p))}</td><td>${esc(lessonStatus(p))}</td><td>${esc(u.name)}</td><td>${us >= stagesFor(u).length ? "Done" : `${us + 1} of ${stagesFor(u).length}`}</td>
          <td>${warmDoneToday(p, u.id) ? "✓" : "—"}</td><td>${streak(p) || "—"}</td><td>${sm.fluent}</td><td>${sm.correct}</td><td>${sm.revisit ? `<span class="warnText">${sm.revisit}</span>` : "0"}</td></tr>`;
      }).join("")}
    </table></div>
    ${rows.length === 0 ? '<p class="muted">No students yet. Add them from the student picker.</p>' : `
    <form class="card" id="floorForm" style="margin-top:18px">
      <h3 style="margin-bottom:6px">Class floor</h3>
      <p class="muted" style="margin:0 0 10px">Everyone works through this lesson by this date. Students who move faster keep going past it.</p>
      <div class="row" style="margin:0"><label>Through <select id="floorLesson">${LEVELS.map((v) => `<optgroup label="${esc(v.name)}">${v.lessons.map((l) => `<option value="${l.id}">${esc(v.name)} · ${lessonName(l)}</option>`).join("")}</optgroup>`).join("")}</select></label>
      <label>by <input id="floorDue" type="date"></label><button class="btn small" type="submit">Set for everyone</button><span class="note" id="floorNote" style="margin:0"></span></div>
    </form>`}
    <section id="detail" style="margin-top:28px"></section>
  </div></main>
  <footer><div class="wrap">The Tigard Method™ · © 2026 Meliesa Tigard</div></footer>`;
  root.querySelector<HTMLButtonElement>("#back")!.onclick = o.onBack;
  void mountStudy(root.querySelector<HTMLElement>("#study")!, o.store);
  root.querySelectorAll<HTMLButtonElement>("[data-open]").forEach((b) => (b.onclick = () => showDetail(b.dataset.open!)));
  const ff = root.querySelector<HTMLFormElement>("#floorForm");
  if (ff) {
    const cur = rows.find((r) => r.p.floor)?.p.floor;
    if (cur) { ff.querySelector<HTMLSelectElement>("#floorLesson")!.value = cur.lesson; if (cur.due) ff.querySelector<HTMLInputElement>("#floorDue")!.value = cur.due; }
    ff.onsubmit = async (e) => {
      e.preventDefault();
      const lesson = ff.querySelector<HTMLSelectElement>("#floorLesson")!.value, due = ff.querySelector<HTMLInputElement>("#floorDue")!.value;
      await Promise.all(rows.map(({ s, p }) => { p.floor = { lesson, ...(due ? { due } : {}) }; return o.store.saveProgress(s.id, p); }));
      ff.querySelector("#floorNote")!.textContent = "Saved.";
    };
  }

  async function showDetail(id: string) {
    const row = rows.find((r) => r.s.id === id);
    if (!row) return;
    const { s, p } = row;
    const box = root.querySelector<HTMLElement>("#detail")!;
    box.innerHTML = `<h2>${esc(s.firstName)}</h2><p class="muted">Loading…</p>`;
    box.scrollIntoView({ block: "start" });
    let attempts: AttemptRow[] = [];
    let skillRows: SkillAttemptRow[] = [];
    try { [attempts, skillRows] = await Promise.all([o.store.listAttempts(s.id, 40), o.store.listSkillAttempts(s.id, 300)]); } catch (e) { console.warn(e); }
    const u = unitById(p.currentUnit), sm = summary(p);
    const tableRows = UNITS.filter((x) => !x.special && !x.op).map((x) => {
      const t = x.tables[0]!, st = p.units[x.id]?.stage ?? 0, n = stagesFor(x).length;
      const intro = UNITS.indexOf(x), cur = UNITS.indexOf(u);
      const status = cur < intro ? "not yet" : cur === intro ? `Stage ${Math.min(st + 1, n)} of ${n}` : `Review: ${PATTERN_STAGES[reviewStageIndex(t, u.id)]!.name}`;
      return `<tr><td>The ${t}s</td><td>${status}</td><td>${st >= n ? "complete" : st > 0 ? `passed Stage ${st}` : "—"}</td></tr>`;
    }).join("");
    box.innerHTML = `
      <h2 style="margin-bottom:4px">${esc(s.firstName)}</h2>
      <p class="muted" style="margin-top:0">${esc(u.name)} · ${warmDoneToday(p, u.id) ? "warm-up done today" : "no warm-up today"} · streak ${streak(p)} · ${sm.fluent} fluent, ${sm.correct} correct, ${sm.revisit} to revisit</p>
      <div class="twocol">
        <div>
          <h3>Fact map</h3>
          <div class="mapwrap"><table class="map">${factMapHTML(p)}</table></div>${LEGEND}
          ${hasDivision(p) ? `<h3 style="margin-top:16px">Division</h3><div class="mapwrap"><table class="map">${factMapHTML(p, "div")}</table></div>` : ""}
          ${LEVELS.map((v) => `<h3 style="margin-top:22px">${esc(v.name)} lessons</h3>
          <div class="mapwrap"><table class="roster"><tr><th>Lesson</th><th>Steps done</th><th>Skipped by showing it</th></tr>${v.lessons.map((l) => { const pr = lessonProgress(p, l), co = p.lessons?.[l.id]?.checkOut; return `<tr><td>${lessonNo(l)}. ${esc(l.title)}</td><td>${pr.done} of ${pr.total}</td><td>${co ? `${co.passed.length} (${esc(co.date)})` : ""}</td></tr>`; }).join("")}</table></div>`).join("")}
          <h3 style="margin-top:22px">Tables</h3>
          <div class="mapwrap"><table class="roster"><tr><th>Table</th><th>Today</th><th>Own lesson</th></tr>${tableRows}</table></div>
        </div>
        <div>
          <h3>Recent answers</h3>
          ${attempts.length === 0 ? '<p class="muted">None yet.</p>' : `<div class="mapwrap"><table class="roster"><tr><th>When</th><th>Fact</th><th>Where</th><th>Result</th><th>Time</th></tr>
            ${attempts.map((a) => `<tr><td>${fmtWhen(a.at)}</td><td>${factText(a)}</td><td>${esc(stageName(a.stage))}</td><td>${a.correct ? (a.firstTry ? "✓" : "✓ (2nd try)") : '<span class="warnText">✗</span>'}${a.usedTool ? " · tool" : ""}</td><td>${a.ms ? (a.ms / 1000).toFixed(1) + " s" : ""}</td></tr>`).join("")}</table></div>`}
          <h3 style="margin-top:22px">Check-ups</h3>
          ${checkHTML(p)}
          <h3 style="margin-top:22px">Where it gets tricky</h3>
          ${trickyHTML(p, skillRows)}
          <h3 style="margin-top:22px">Facts check (research pre/post)</h3>
          ${factsHTML(p)}
          <h3 style="margin-top:22px">Placement diagnostic</h3>
          ${diagHTML(p)}
          <h3 style="margin-top:22px">Starting point</h3>
          <p class="muted" style="margin:0 0 8px">Where the diagnostic placed ${esc(s.firstName)} — not where the grade says. Lessons before it stay open. Tigard Times is taught as is wherever it falls.</p>
          <form class="row" id="placeForm" style="margin:0"><select id="placeLesson"><option value="">The beginning (Level 3, Lesson 1)</option>${PLACE_OPTS}</select><button class="btn small" type="submit">Save</button><span class="note" id="placeNote" style="margin:0">${p.placement ? `Set by ${p.placement.by}${p.placement.date ? `, ${esc(p.placement.date)}` : ""}.` : ""}</span></form>
          <h3 style="margin-top:22px">Account</h3>
          <div class="row"><button type="button" class="btn ghost small" id="practiceAs">Open ${esc(s.firstName)}'s practice</button><button type="button" class="btn ghost small" id="remove">Remove student</button></div>
          <p class="note" id="removeNote" hidden>This deletes ${esc(s.firstName)} and all of their progress. Click again to confirm.</p>
        </div>
      </div>`;
    box.querySelector<HTMLButtonElement>("#practiceAs")!.onclick = () => o.onOpenStudent(s);
    const pf = box.querySelector<HTMLFormElement>("#placeForm")!, ps = pf.querySelector<HTMLSelectElement>("#placeLesson")!;
    ps.value = p.placement?.lesson ?? "";
    pf.onsubmit = async (e) => {
      e.preventDefault();
      if (ps.value) p.placement = { lesson: ps.value, by: "teacher", date: new Date().toISOString().slice(0, 10) }; else delete p.placement;
      await o.store.saveProgress(s.id, p);
      pf.querySelector("#placeNote")!.textContent = "Saved.";
    };
    let armed = false;
    const rb = box.querySelector<HTMLButtonElement>("#remove")!;
    rb.onclick = async () => {
      if (!armed) { armed = true; box.querySelector<HTMLElement>("#removeNote")!.hidden = false; setTimeout(() => { armed = false; box.querySelector<HTMLElement>("#removeNote")!.hidden = true; }, 5000); return; }
      await o.store.removeStudent(s.id);
      mountTeacher(root, o);
    };
  }
}

const PLACE_OPTS = LEVELS.map((v) => `<optgroup label="${esc(v.name)}">${v.lessons.map((l) => `<option value="${l.id}">${esc(v.name)} · ${lessonName(l)}: ${esc(l.title)}</option>`).join("")}</optgroup>`).join("");

/** "L3 Lesson 3 · 4 of 9" — the next lesson from the starting point. */
function lessonStatus(p: StudentProgress): string {
  const l = upNextLesson(p);
  if (!l) return "all built lessons done";
  const pr = lessonProgress(p, l);
  return `L${l.level} ${lessonName(l)} · ${pr.done} of ${pr.total}${p.placement ? ` (from L${anyLesson(p.placement.lesson)?.level} ${anyLesson(p.placement.lesson) ? lessonName(anyLesson(p.placement.lesson)!) : ""})` : ""}`;
}

/** The mistakes this student makes most, with what the student did and what to say — the teacher's follow-up list. */
function trickyHTML(p: StudentProgress, rows: SkillAttemptRow[]): string {
  const counts = new Map<string, number>();
  for (const r of rows) if (r.misconception && r.misconception !== "skipped") counts.set(r.misconception, (counts.get(r.misconception) ?? 0) + 1);
  const stuck = Object.entries(p.skills ?? {}).filter(([, r]) => (r.stuck ?? 0) > 0 && !r.mastered);
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (!top.length && !stuck.length) return '<p class="muted">Nothing yet.</p>';
  return `${stuck.length ? `<p><b>Not yet mastered after a full practice set:</b> ${stuck.map(([id]) => esc(SKILLS[id]?.name ?? id)).join(", ")}.</p>` : ""}
    ${top.length ? `<div class="mapwrap"><table class="roster"><tr><th>Times</th><th>What happened</th><th>What to say</th></tr>${top.map(([c, n]) => `<tr><td>${n}</td><td>${esc(MISCONCEPTIONS[c]?.did ?? c)}</td><td>${esc(MISCONCEPTIONS[c]?.say ?? "")}</td></tr>`).join("")}</table></div>` : ""}`;
}

/** The method-neutral facts check: beginning and end sittings, by operation and by table. */
export function factsHTML(p: StudentProgress): string {
  const ft = p.factsTest;
  const pre = ft?.pre, post = ft?.post;
  if (!pre || !taken(pre)) return `<p class="muted">${pre ? `In progress: ${Object.keys(pre.items).length} of 63 answered; it resumes where it stopped.` : "Not taken yet. Optional for FocalPoint students (they've had a diagnostic); required first for a class member."}</p>`;
  const a = factsSummary(pre), z = post && taken(post) ? factsSummary(post) : undefined;
  const cell = (x: { right: number; n: number; fluent: number }) => `${x.right}/${x.n} · ${x.fluent} fast`;
  const row = (name: string, s1: { right: number; n: number; fluent: number }, s2?: { right: number; n: number; fluent: number }) => `<tr><td>${name}</td><td>${cell(s1)}</td><td>${s2 ? cell(s2) : "—"}</td></tr>`;
  return `<p style="margin:0 0 6px">Beginning ${esc(pre.date)}${z ? `; end ${esc(post!.date)}` : ""}. "Fast" = right in under 3 seconds. Median time ${(a.all.medianMs / 1000).toFixed(1)} s${z ? ` → ${(z.all.medianMs / 1000).toFixed(1)} s` : ""}.</p>
    <div class="mapwrap"><table class="roster"><tr><th></th><th>Beginning</th><th>End</th></tr>
    ${row("<b>All</b>", a.all, z?.all)}${row("Multiplication", a.mul, z?.mul)}${row("Division", a.div, z?.div)}
    ${TABLES.map((t) => row(`${t}s`, a.byTable[t]!, z?.byTable[t])).join("")}</table></div>`;
}

function diagHTML(p: StudentProgress): string {
  const d = p.diagnostic;
  if (!d) return '<p class="muted">Not taken yet. It is the first card on the student\'s page ("Find my starting point").</p>';
  const placed = anyLesson(d.placed);
  const rows = Object.entries(d.probes).map(([id, r]) => { const l = anyLesson(id); return `<tr><td>${l ? `L${l.level} ${lessonName(l)}: ${esc(l.title)}` : esc(id)}</td><td>${r} of 2</td></tr>`; }).join("");
  return `<p style="margin:0 0 6px">${esc(d.date)}: placed at <b>${placed ? `Level ${placed.level}, ${lessonName(placed)}` : esc(d.placed)}</b>. Times tables: ${d.facts.right} of ${d.facts.n}${d.solid ? ` — solid, so the times-tables units are skipped${d.divSolid ? " (division too)" : "; division units are still taught"}` : " — not solid yet, so Tigard Times from the start, taught as is"}.</p>
    ${rows ? `<div class="mapwrap"><table class="roster"><tr><th>Lesson checked</th><th>Right</th></tr>${rows}</table></div>` : ""}`;
}

const itemsOf = (l?: Lesson) => (l ? (l.steps[0] as { items: CheckItem[] }).items : []);
const resultOf = (p: StudentProgress, l?: Lesson) => (l ? p.checks?.[l.steps[0]!.id] : undefined);

function checkLine(p: StudentProgress): string {
  const parts: string[] = [];
  for (const v of LEVELS) {
    const a = resultOf(p, v.pre), b = resultOf(p, v.post);
    const sa = a?.date ? checkScore(itemsOf(v.pre), a.results) : null, sb = b?.date ? checkScore(itemsOf(v.post), b.results) : null;
    if (sa || sb) parts.push(`L${v.id} ${sa ? `pre ${sa.right}/${sa.total}` : "pre —"}${sb ? ` · post ${sb.right}/${sb.total}` : ""}`);
  }
  return parts.join("; ") || "—";
}

/** Per level: pre and post side by side by domain, and the lessons to re-teach from the latest check-up's misses. */
function checkHTML(p: StudentProgress): string {
  const out: string[] = [];
  for (const v of LEVELS) {
    const pre = resultOf(p, v.pre), post = resultOf(p, v.post);
    if (!pre?.date && !post?.date) continue;
    const PI = itemsOf(v.pre), QI = itemsOf(v.post);
    const a = pre?.date ? checkScore(PI, pre.results) : null, b = post?.date ? checkScore(QI, post.results) : null;
    const latest = post?.date ? { items: QI, r: post.results } : { items: PI, r: pre!.results };
    const reteach = [...new Set(latest.items.filter((it) => latest.r[it.id] !== true).map((it) => it.lesson))].sort((x, y) => x - y);
    out.push(`<h4 style="margin:10px 0 4px">${esc(v.name)}</h4><div class="mapwrap"><table class="roster"><tr><th>Domain</th><th>Beginning</th><th>End</th></tr>
    ${DOMAINS.filter((d) => a?.byDomain[d]!.total || b?.byDomain[d]!.total).map((d) => `<tr><td>${esc(DOMAIN_NAMES[d]!)}</td><td>${a ? `${a.byDomain[d]!.right}/${a.byDomain[d]!.total}` : "—"}</td><td>${b ? `${b.byDomain[d]!.right}/${b.byDomain[d]!.total}` : "—"}</td></tr>`).join("")}
    <tr><td><b>Total</b></td><td><b>${a ? `${a.right}/${a.total}` : "—"}</b></td><td><b>${b ? `${b.right}/${b.total}` : "—"}</b></td></tr></table></div>
    ${reteach.length ? `<p class="note">Lessons to look at again (from the ${post?.date ? "end" : "beginning"} check-up): ${reteach.map((n) => { const lv = !post?.date && v.preFrom ? v.preFrom : v.id, l = LEVELS.find((x) => x.id === lv)?.lessons.find((x) => x.num === n); return `${!post?.date && v.preFrom ? `Level ${v.preFrom} ` : ""}${l ? lessonName(l) : `Lesson ${n}`}`; }).join(", ")}.</p>` : ""}`);
  }
  return out.join("") || '<p class="muted">Not taken yet. The Beginning Check-Up is the first card on each level\'s page.</p>';
}
